import { html, property } from 'lit-element';
import { nothing } from 'lit-html';
import { classMap } from 'lit-html/directives/class-map';
import {
  event,
  EventEmitter,
  KatLitElement,
  register,
} from '../../shared/base';
import { checkSlots } from '../../shared/slot-utils';
import baseStyles from '../../shared/base/base.lit.scss';
import styles from './metric.lit.scss';

const MAX_CHILD_SIMPLE_METRICS = 4;

const TREND_ICON_MAP = {
  increase: 'trending_up',
  decrease: 'trending_down',
  neutral: 'trending_neutral',
  missing: 'minus',
};

/**
 * @component {kat-metric} KatMetric Standardizes the display and layout of metrics, including complex metrics (that combine multiple “child” metrics) and those with visualizations.
 * @slot control Single control element for the metric, typically a dropdown or toggle.
 * @slot actions Actions available for the metric.
 * @slot amount Amount available for the metric, supersedes the amount attribute.
 * @slot visualization Visualization for the metric.
 * @subcomponent ./metric-simple.ts
 * @example Default { "header": "Primary metric label", "subheader": "Comparision point", "metric": "$1,234.00", "trend": "increase", "trend-value": "+100%", "trend-polarity": "negative", "content": "<kat-metric-visualization>Slot: Visualization</kat-metric-visualization><kat-metric-control><kat-button size=\"small\" variant=\"utility\"><kat-icon name=\"more_horiz\" size=\"xsmall\"></kat-icon></kat-button></kat-metric-control><kat-metric-actions><kat-button variant=\"tertiary\">Action button</kat-button><kat-button variant=\"primary\">Action button</kat-button></kat-metric-actions>" }
 * @example WithSimpleMetrics { "header": "Primary metric label", "subheader": "Comparision point", "trend": "increase", "trend-value": "+100%", "trend-polarity": "negative", "content": "<kat-metric-amount><kat-popover position=\"right\" trigger-type=\"hover\"><kat-link href=\"#\" slot=\"trigger\" variant=\"hover-target\">$1,234.00</kat-link>Detailed metric information.</kat-popover></kat-metric-amount><kat-metric-visualization>Slot: Visualization</kat-metric-visualization><kat-metric-control><kat-button size=\"small\" variant=\"utility\"><kat-icon name=\"more_horiz\" size=\"xsmall\"></kat-icon></kat-button></kat-metric-control><kat-metric-actions><kat-button variant=\"tertiary\">Action button</kat-button><kat-button variant=\"primary\">Action button</kat-button></kat-metric-actions><kat-metric-simple header=\"Child label 1\" subheader=\"Comparision point 1\" metric=\"$1,234.00\" trend=\"increase\" trend-value=\"+100%\" trend-polarity=\"positive\"></kat-metric-simple><kat-metric-simple header=\"Child label 2\" subheader=\"Comparision point 2\" metric=\"$500\" trend=\"decrease\" trend-value=\"-50%\" trend-polarity=\"negative\"></kat-metric-simple><kat-metric-simple header=\"Child label 3\" subheader=\"Comparision point 3\" metric=\"$0\" trend=\"neutral\" trend-value=\"0%\" trend-polarity=\"neutral\"></kat-metric-simple><kat-metric-simple header=\"Child label 4\" subheader=\"Comparision point 4\" metric=\"$0\" trend=\"missing\" trend-value=\"N/A\" trend-polarity=\"neutral\"></kat-metric-simple>" }
 * @a11y {keyboard}
 * @a11y {contrast}
 * @a11y {sr}
 */
@register('kat-metric')
export class KatMetric extends KatLitElement {
  static get styles() {
    return [baseStyles, styles];
  }

  // Mapping of a component/slot name to their allowed components
  static slotMapping = {
    actions: {
      name: 'kat-metric-actions',
      allowed: [],
    },
    amount: {
      name: 'kat-metric-amount',
      allowed: [],
    },
    control: {
      name: 'kat-metric-control',
      allowed: [],
    },
    visualization: {
      name: 'kat-metric-visualization',
      allowed: [],
    },
  };

  /** Whether the header should be clickable. */
  @property({ attribute: 'header-clickable' })
  headerClickable? = false;

  /** The title of the module, summarizing the content it contains. May be linked in order to take users to a new page. */
  @property()
  header: string;

  /** A secondary text area for secondary or clarifying information, such as a "last updated" timestamp. */
  @property()
  subheader?: string;

  /** The primary metric value to be displayed. */
  @property()
  metric: string;

  /** Optional trend value to be display */
  @property({ attribute: 'trend-value' })
  trendValue?: string;

  /**
   * The trend of a metric being displayed.
   * @enum {value} increase Increase in the observed value.
   * @enum {value} decrease Decrease in the observed value.
   * @enum {value} neutral No variation in the observed value.
   * @enum {value} missing No data is available.
   */
  @property()
  trend?: 'increase' | 'decrease' | 'neutral' | 'missing';

  /**
   * The polarity of the trend line.
   * @enum {value} positive The observed value has positive impact.
   * @enum {value} negative The observed value has negative impact.
   * @enum {value} neutral The observed value has no impact.
   */
  @property({ attribute: 'trend-polarity' })
  trendPolarity?: 'positive' | 'negative' | 'neutral' = 'neutral';

  /** Emits when header is clicked.  Requires `clickable` to be set to true. */
  @event('clicked', true)
  private _clicked: EventEmitter;

  private _handleClick(e: MouseEvent) {
    e.preventDefault();
    this._clicked.emit();
  }

  private _renderHeader() {
    if (!this.header) {
      return nothing;
    }

    const renderedHeader = this.headerClickable
      ? html`<kat-link href="#" @click=${this._handleClick}
          >${this.header}</kat-link
        >`
      : this.header;

    return html`<span class="header">${renderedHeader}</span>`;
  }

  private renderSubheader() {
    return this.subheader
      ? html`<div class="subheader">${this.subheader}</div>`
      : nothing;
  }

  private _renderMetricAmount() {
    return html`<div class="metric-amount">
      <slot name=${KatMetric.slotMapping.amount.name}>${this.metric}</slot>
    </div>`;
  }

  private _renderTrend() {
    const renderedTrendIcon = TREND_ICON_MAP[this.trend]
      ? html`<kat-icon
          name="${TREND_ICON_MAP[this.trend]}"
          size="small"
        ></kat-icon>`
      : nothing;

    const renderedTrendAmount = this.trendValue
      ? html`<div class="trend-amount">${this.trendValue}</div>`
      : nothing;

    return this.trendValue || TREND_ICON_MAP[this.trend]
      ? html`<div
          class="${classMap({ trend: true, [this.trendPolarity]: true })}"
        >
          ${renderedTrendAmount}${renderedTrendIcon}
        </div>`
      : nothing;
  }

  private _renderSlotActions() {
    return checkSlots(this)[KatMetric.slotMapping.actions.name]
      ? html`<section class="actions">
          <slot name=${KatMetric.slotMapping.actions.name}></slot>
        </section>`
      : nothing;
  }

  private _renderSlotControl() {
    return checkSlots(this)[KatMetric.slotMapping.control.name]
      ? html`<section class="control">
          <slot name=${KatMetric.slotMapping.control.name}></slot>
        </section>`
      : nothing;
  }

  private _renderSlotVisualization() {
    return checkSlots(this)[KatMetric.slotMapping.visualization.name]
      ? html`<section class="visualization">
          <slot name=${KatMetric.slotMapping.visualization.name}></slot>
        </section>`
      : nothing;
  }

  private _renderSimpleMetrics() {
    const simpleMetrics = Array.from(this.querySelectorAll('kat-metric-simple'))
      .slice(0, MAX_CHILD_SIMPLE_METRICS)
      .map(
        simpleMetric => html`<kat-grid-item>${simpleMetric}</kat-grid-item>`
      );

    return simpleMetrics.length > 0
      ? html`<div class="simple">
          <kat-grid cols-xxl="2" cols-xl="2" cols-l="2" cols-m="2" cols-s="2"
            >${simpleMetrics}</kat-grid
          >
        </div>`
      : nothing;
  }

  render() {
    const complexClassMap = classMap({
      complex: true,
      // Undocumented attribute
      // Only used to indicate this kat-metric was created from a kat-metric-simple.
      nested: this.hasAttribute('simple'),
    });

    return html`<div class="wrapper">
      <div class=${complexClassMap}>
        <div class="header-wrapper">
          ${this._renderHeader()}${this._renderSlotControl()}
        </div>
        <div class="metric">
          ${this._renderMetricAmount()}${this._renderTrend()}
        </div>
        ${this.renderSubheader()} ${this._renderSlotVisualization()}
        ${this._renderSlotActions()}
      </div>
      ${this._renderSimpleMetrics()}
    </div>`;
  }
}
